import { Box } from "@mui/material";
import { categoryColor } from "./scheduleModel";

export const TOKENS = {
  ink: "var(--color-ink)",
  muted: "var(--color-accent-violet-mid)",
  hairline: "var(--color-hairline-cloud)",
  hairlineCool: "var(--color-hairline-cool)",
  primary: "var(--color-primary)",
  primaryDeep: "var(--color-ink-deep)",
  violet: "var(--color-accent-violet)",
  press: "var(--color-surface-press-light)",
  wash: "#fafafb",
  warn: "var(--color-warn)",
  danger: "var(--color-danger)",
  success: "var(--color-success)",
};

const T = TOKENS;

export function Icon({ size = 13, children }) {
  return (
    <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" sx={{ flex: "0 0 auto", display: "block" }}>
      {children}
    </Box>
  );
}

export const ICONS = {
  cap: <><path d="M22 10 12 5 2 10l10 5Z" /><path d="M6 12v5c3 3 9 3 12 0v-5" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  pin: <><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>,
  cal: <><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  alert: <><path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z" /><path d="M12 9v4M12 17h.01" /></>,
  lock: <><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  layers: <><path d="m12 2 10 5-10 5L2 7Z" /><path d="m2 17 10 5 10-5M2 12l10 5 10-5" /></>,
  list: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
};

const TONE_COLOR = { neutral: T.muted, warn: T.warn, ok: T.success, bad: T.danger };

/** Quiet inline text with an optional leading icon; colour carries the meaning, there is no chip around it. */
export const outlineBtnSx = {
  height: 32, px: "12px", borderRadius: "10px", fontSize: 11, fontWeight: 600, letterSpacing: ".2px", textTransform: "uppercase",
  whiteSpace: "nowrap", bgcolor: "#fff", color: T.ink, border: `1px solid ${T.hairlineCool}`, "&:hover": { borderColor: T.ink, bgcolor: T.press },
};

export function Note({ tone = "neutral", icon, children, sx }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", color: TONE_COLOR[tone], ...sx }}>
      {icon && <Icon size={13}>{icon}</Icon>}
      <span>{children}</span>
    </Box>
  );
}

/** A line of facts separated by middots, e.g. "Firefighter · Lead instructor needed". */
export function FactLine({ children, sx }) {
  const items = (Array.isArray(children) ? children : [children]).filter(Boolean);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px 8px", fontSize: 13, color: T.muted, ...sx }}>
      {items.map((it, i) => (
        <Box key={i} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          {i > 0 && <Box component="span" aria-hidden="true" sx={{ color: T.hairlineCool }}>·</Box>}
          {it}
        </Box>
      ))}
    </Box>
  );
}

const CATEGORY_LABEL = { FIREFIGHTER: "Firefighter", RESCUE: "Rescue", EMS: "EMS", AHA: "AHA", OTHER: "Other" };
const categoryLabel = (c) => CATEGORY_LABEL[String(c || "").toUpperCase()] || (c ? String(c) : "Other");

export function CategoryLabel({ category }) {
  const color = categoryColor(category);
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", color: T.ink, fontWeight: 500 }}>
      <Box component="i" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color }} />
      {categoryLabel(category)}
    </Box>
  );
}

export function CategoryTile({ color }) {
  return (
    <Box aria-hidden="true" sx={{ width: 44, height: 44, borderRadius: "10px", display: "grid", placeItems: "center", flex: "0 0 auto",
      color, bgcolor: `color-mix(in oklch, ${color} 11%, #fff)` }}>
      <Icon size={20}>{ICONS.cap}</Icon>
    </Box>
  );
}
